/* Traceability Event detail page — Phase 4
 *
 * Read-only review of a saved Traceability Event. Shows the original
 * snapshot taken at save time (summary tiles, affected make-days / cheese
 * batches / dispatches / customers, internal notes, status history and
 * customer-communication drafts) and a single primary action: Export Full
 * Response Pack PDF. Users can also re-open the event in the Mock Recall
 * workspace to continue editing (Phase 3 flow).
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getTraceEvent, downloadTraceEventPdf, recallReport, updateTraceEvent } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Download, Edit, RefreshCw, ShieldAlert,
  User, Truck, FlaskConical, Boxes, PackageOpen, FileText, Clock, AlertTriangle,
} from 'lucide-react';

const EVENT_STATUSES = [
  { value: 'draft',          label: 'Draft',                   color: 'bg-zinc-200 text-zinc-700' },
  { value: 'under_review',   label: 'Under review',            color: 'bg-amber-100 text-amber-800' },
  { value: 'approved',       label: 'Approved internally',     color: 'bg-blue-100 text-blue-800' },
  { value: 'comms_prepared', label: 'Customer comms prepared', color: 'bg-purple-100 text-purple-800' },
  { value: 'closed',         label: 'Closed',                  color: 'bg-emerald-100 text-emerald-800' },
];

function pill(value) {
  const s = EVENT_STATUSES.find(x => x.value === value);
  if (!s) return <Badge variant="outline">{value || '—'}</Badge>;
  return <Badge className={`${s.color} border-0 font-normal`}>{s.label}</Badge>;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function TraceEventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [recall, setRecall] = useState(null);
  // Status change controls
  const [newStatus, setNewStatus] = useState(null);
  const [statusNote, setStatusNote] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  const loadEvent = async (eventId) => {
    setLoading(true);
    try {
      const r = await getTraceEvent(eventId);
      setEvent(r.data);
      setNewStatus(r.data?.status || 'draft');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load event');
    } finally { setLoading(false); }
  };

  const loadRecall = async (ev) => {
    if (!ev?.starting_type || !ev?.starting_value) return;
    setRefreshing(true);
    try {
      const r = await recallReport({ [ev.starting_type]: ev.starting_value });
      setRecall(r.data);
    } catch (e) {
      // Soft-fail: keep snapshot view
      console.warn('recall refresh failed', e);
    } finally { setRefreshing(false); }
  };

  useEffect(() => {
    if (id) loadEvent(id);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [id]);

  useEffect(() => {
    if (event) loadRecall(event);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [event?.id]);

  const exportPdf = async () => {
    if (!event) return;
    setDownloading(true);
    try {
      const r = await downloadTraceEventPdf(event.id);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.event_ref || event.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF export failed');
    } finally { setDownloading(false); }
  };

  const saveStatus = async () => {
    if (!event || !newStatus || newStatus === event.status) {
      toast.info('No status change to save');
      return;
    }
    setSavingStatus(true);
    try {
      const payload = {
        starting: event.starting,
        starting_type: event.starting_type,
        starting_value: event.starting_value,
        summary_snapshot: event.summary_snapshot || {},
        affected_customers: event.affected_customers || [],
        status: newStatus,
        status_change_note: statusNote.trim() || undefined,
        internal_notes: event.internal_notes || [],
        customer_drafts: event.customer_drafts || [],
      };
      const r = await updateTraceEvent(event.id, payload);
      setEvent(r.data);
      setStatusNote('');
      toast.success('Status updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status update failed');
    } finally { setSavingStatus(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="trace-event-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading event…
      </div>
    );
  }
  if (!event) {
    return (
      <div className="text-center py-24" data-testid="trace-event-missing">
        <div className="text-lg font-medium mb-2">Event not found</div>
        <Button variant="outline" onClick={() => navigate('/traceability/events')}>
          <ArrowLeft className="w-4 h-4 mr-1" />Back to events
        </Button>
      </div>
    );
  }

  // Prefer refreshed recall data, fall back to snapshot
  const s = event.summary_snapshot || {};
  const customers = (recall?.affected_customers) || event.affected_customers || [];
  const makeDays = (recall?.make_days) || [];
  const batches = (recall?.cheese_batches) || [];
  const downstream = (recall?.downstream) || [];
  const drafts = event.customer_drafts || [];
  const notes = event.internal_notes || [];
  const history = event.status_history || [];

  const tiles = [
    { label: 'Make Days affected', value: s.make_days_count ?? makeDays.length, icon: FlaskConical },
    { label: 'Cheese batches',     value: s.cheese_batches_count ?? batches.length, icon: Boxes },
    { label: 'Dispatches',         value: s.dispatches_count ?? downstream.length, icon: Truck },
    { label: 'Customers affected', value: s.customers_count ?? customers.length, icon: User },
    { label: 'Total qty dispatched',
      value: (s.total_dispatched_quantity || 0).toLocaleString(), icon: PackageOpen },
  ];

  return (
    <div className="space-y-4" data-testid="trace-event-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/traceability/events" className="text-xs text-blue-700 hover:underline flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" />Events
            </Link>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              <span className="font-mono">{event.event_ref || event.id.slice(0, 8)}</span>
            </h1>
            {pill(event.status)}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Trigger: <span className="font-medium">{event.starting || '—'}</span>
            {event.starting_value && <span className="font-mono ml-2 text-xs">({event.starting_value})</span>}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Created {fmtDate(event.created_at)}{event.updated_at && ` · last updated ${fmtDate(event.updated_at)}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => loadRecall(event)} disabled={refreshing} data-testid="btn-refresh-data">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh data
          </Button>
          <Link to={`/traceability/recall?reopen=${event.id}`}>
            <Button variant="outline" data-testid="btn-edit-event">
              <Edit className="w-4 h-4 mr-1" />Edit in Mock Recall
            </Button>
          </Link>
          <Button onClick={exportPdf} disabled={downloading} data-testid="btn-export-pdf">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            Export Full Response Pack
          </Button>
        </div>
      </div>

      {/* DRAFT banner */}
      <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 text-sm flex gap-2" data-testid="draft-banner">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
        <div>
          <span className="font-semibold">Draft response pack.</span> This page is intended for internal
          review and audit evidence. Customer communications below are <strong>drafts only</strong> —
          they must be reviewed by QA / management / legal / insurer before any are sent. Keystone
          never sends customer email automatically.
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="summary-tiles">
        {tiles.map((t, i) => (
          <Card key={i}><CardContent className="pt-4">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
              <t.icon className="w-3 h-3" />{t.label}
            </div>
            <div className="text-2xl font-semibold mt-1">{t.value ?? '—'}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Status timeline */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-zinc-500" />
            <h3 className="font-semibold">Status timeline</h3>
            <Badge variant="outline" className="ml-auto text-xs">{history.length} entries</Badge>
          </div>
          {history.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No status changes recorded yet.</div>
          ) : (
            <ol className="border-l-2 border-zinc-200 pl-4 space-y-3" data-testid="status-history">
              {history.map((h, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-amber-500 border-2 border-white" />
                  <div className="text-sm">
                    {h.old_status
                      ? <>{pill(h.old_status)} <span className="mx-1 text-zinc-400">→</span> {pill(h.new_status)}</>
                      : <>Event created with status {pill(h.new_status)}</>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {fmtDate(h.changed_at)} · {h.changed_by || '—'}
                  </div>
                  {h.note && <div className="text-sm mt-1 bg-zinc-50 border rounded px-2 py-1">{h.note}</div>}
                </li>
              ))}
            </ol>
          )}

          {/* Status change control */}
          <div className="border-t mt-4 pt-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Change status</div>
            <div className="grid md:grid-cols-3 gap-3 items-end">
              <div>
                <Select value={newStatus || event.status} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENT_STATUSES.map(st => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                rows={2}
                placeholder="Optional note (e.g. reason for transition)…"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                data-testid="status-note"
              />
              <Button
                onClick={saveStatus}
                disabled={savingStatus || newStatus === event.status}
                data-testid="btn-save-status"
              >
                {savingStatus && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                Save status change
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Affected customers */}
      {customers.length > 0 && (
        <Card><CardContent className="pt-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><User className="w-4 h-4" />Affected customers</h3>
          <div className="flex flex-wrap gap-2" data-testid="affected-customers">
            {customers.map((c, i) => (
              <Badge key={i} variant="outline" className="text-sm px-3 py-1.5">
                {c.name || '—'}
                {(c.city || c.delivery_city) && <span className="text-muted-foreground ml-2">· {c.city || c.delivery_city}</span>}
              </Badge>
            ))}
          </div>
        </CardContent></Card>
      )}

      {/* Make days */}
      {makeDays.length > 0 && (
        <Card><CardContent className="pt-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><FlaskConical className="w-4 h-4" />Make Days affected</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border rounded" data-testid="make-days-table">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr>
                <th className="text-left px-3 py-2">Ref</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Supervisor</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr></thead>
              <tbody>{makeDays.map((m, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{m.make_day_ref}</td>
                  <td className="px-3 py-2">{m.make_date}</td>
                  <td className="px-3 py-2">{m.product_type || '—'}</td>
                  <td className="px-3 py-2">{m.supervisor || '—'}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{m.status}</Badge></td>
                </tr>))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}

      {/* Cheese batches */}
      {batches.length > 0 && (
        <Card><CardContent className="pt-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><Boxes className="w-4 h-4" />Cheese batches produced</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border rounded" data-testid="batches-table">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr>
                <th className="text-left px-3 py-2">Batch</th>
                <th className="text-left px-3 py-2">Make Day</th>
                <th className="text-left px-3 py-2">Use-by</th>
                <th className="text-right px-3 py-2">Qty</th>
              </tr></thead>
              <tbody>{batches.map((b, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{b.batch_number || b.batch_code}</td>
                  <td className="px-3 py-2 font-mono text-xs">{b.make_day_ref || '—'}</td>
                  <td className="px-3 py-2">{b.use_by_date || '—'}</td>
                  <td className="px-3 py-2 text-right">{b.quantity || '—'}</td>
                </tr>))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}

      {/* Downstream dispatches */}
      {downstream.length > 0 && (
        <Card><CardContent className="pt-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><Truck className="w-4 h-4" />Downstream dispatches</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border rounded" data-testid="dispatches-table">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr>
                <th className="text-left px-3 py-2">Dispatch</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">City</th>
                <th className="text-left px-3 py-2">Sales orders</th>
              </tr></thead>
              <tbody>{downstream.map((row, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{row.dispatch?.reference || row.dispatch?.dispatch_ref || row.dispatch?.id?.slice(0, 8)}</td>
                  <td className="px-3 py-2">{row.dispatch?.dispatch_date || '—'}</td>
                  <td className="px-3 py-2">{row.customer?.name || '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{row.customer?.delivery_city || '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{(row.sales_orders || []).map(so => so.order_number).join(', ') || '—'}</td>
                </tr>))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}

      {/* Internal notes */}
      <Card>
        <CardContent className="pt-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><FileText className="w-4 h-4" />Internal notes</h3>
          {notes.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No internal notes recorded for this event.</div>
          ) : (
            <ul className="space-y-2" data-testid="internal-notes">
              {notes.map((n, i) => (
                <li key={n.id || i} className="bg-zinc-50 border rounded p-2 text-sm">
                  <div className="text-xs text-muted-foreground mb-0.5">
                    {n.author || 'team'} · {fmtDate(n.created_at)}
                  </div>
                  <div className="whitespace-pre-wrap">{n.note}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Customer drafts */}
      <Card>
        <CardContent className="pt-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><User className="w-4 h-4" />Customer Communication Drafts</h3>
          {drafts.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No drafts generated yet. Open this event in Mock Recall to generate drafts.</div>
          ) : (
            <ul className="space-y-3" data-testid="customer-drafts">
              {drafts.map((d, i) => (
                <li key={d.id || i} className="border rounded p-3">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <div className="font-medium">{d.customer_name || '—'}</div>
                    <div className="text-xs text-muted-foreground font-mono">{d.dispatch_ref || '—'} · {d.dispatch_date || '—'}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mb-2">
                    Product: {d.affected_product || '—'} · Batch: <span className="font-mono">{d.affected_batch || '—'}</span> · Qty: {d.quantity_supplied ?? '—'}
                  </div>
                  {d.explanation && (
                    <div className="text-sm mb-2"><span className="font-medium">Explanation: </span>{d.explanation}</div>
                  )}
                  {d.requested_action && (
                    <div className="text-sm"><span className="font-medium">Requested action: </span>{d.requested_action}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
